import type { CameraSnapshot } from './CaptureService';
import type { ImageSize, RenderQualityTier } from './ImageSpec';
import type { SceneManifest } from './SceneManifest';
import type { EnvironmentState } from '@/environment/EnvironmentState';

/**
 * What the photo gallery needs from a place to keep shots.
 *
 * `PhotoStore` (IndexedDB) and `CloudPhotoStore` (the account) both satisfy this, and the gallery,
 * the enhance panel, and photo mode are written against the interface.
 *
 * The one thing that could not stay as it was is how an image is held. Locally a shot's four passes
 * are `Blob`s sitting in IndexedDB, immediately available. In an account they are objects behind
 * authenticated URLs, and a gallery of forty shots is on the order of a gigabyte — downloading all
 * of it before the first thumbnail can render would make the hosted app unusable. So an image is a
 * {@link ShotImage}: a URL that can go straight into an `<img src>` without transferring anything,
 * plus a `blob()` for the two places that genuinely need the bytes — re-sending a capture to the
 * AI, and saving a file to disk.
 *
 * That distinction is the whole reason this interface exists rather than the concrete class being
 * passed around. It also improves the local path: the gallery no longer mints an object URL per
 * render pass on every re-render, because the store hands out one URL per image and owns its
 * lifetime.
 */
export interface PhotoStorage {
  /** Whether a shot saved now will survive a reload. */
  readonly persistent: boolean;

  save(shot: NewShot): Promise<StoredShot>;

  /** All shots, newest first. */
  list(): Promise<StoredShot[]>;

  get(id: string): Promise<StoredShot | undefined>;

  /** Attach an enhanced render to a shot. Returns the updated record. */
  addVariant(shotId: string, variant: NewVariant): Promise<StoredShot | undefined>;

  removeVariant(shotId: string, variantId: string): Promise<StoredShot | undefined>;

  rename(shotId: string, title: string): Promise<StoredShot | undefined>;

  remove(id: string): Promise<void>;

  clear(): Promise<void>;

  /** Approximate bytes held by all stored images, for a storage readout. */
  usageBytes(): Promise<number>;

  close(): void;
}

/**
 * One stored image.
 *
 * `url` is displayable immediately and costs nothing to produce — an object URL for a local blob,
 * an authenticated API path for a stored one. `blob()` is the expensive operation and is called
 * only where the bytes themselves are required.
 *
 * The store owns the lifetime of `url`; callers must not revoke it. That is what lets a card
 * re-render without leaking an object URL per pass, which the previous code did.
 */
export interface ShotImage {
  readonly url: string;
  blob(): Promise<Blob>;
}

export interface StoredVariant {
  id: string;
  createdAt: number;
  /** Style preset used, kept by id and label so the gallery reads well after a preset is renamed. */
  presetId: string;
  presetLabel: string;
  prompt: string;
  model: string;
  quality: RenderQualityTier;
  size: ImageSize;
  image: ShotImage;
  estimatedCostUsd: number;
}

export interface StoredShot {
  id: string;
  createdAt: number;
  title: string;
  size: ImageSize;
  beauty: ShotImage;
  idMap: ShotImage;
  depth: ShotImage;
  thumbnail: ShotImage;
  manifest: SceneManifest;
  /** Restores the exact frame — used by "recall" so a shot can be retaken after edits. */
  camera: CameraSnapshot;
  environment: EnvironmentState;
  variants: StoredVariant[];
}

/**
 * A shot on its way in.
 *
 * Raw `Blob`s, not {@link ShotImage}s: this is what the renderer just produced, and the store is
 * what turns it into something addressable.
 */
export interface NewShot {
  title: string;
  size: ImageSize;
  beauty: Blob;
  idMap: Blob;
  depth: Blob;
  thumbnail: Blob;
  manifest: SceneManifest;
  camera: CameraSnapshot;
  environment: EnvironmentState;
}

/** A variant on its way in, before the store gives it an id and a home. */
export interface NewVariant extends Omit<StoredVariant, 'id' | 'createdAt' | 'image'> {
  image: Blob;
}

/**
 * Wrap an in-memory blob as a {@link ShotImage}, minting the object URL on first use.
 *
 * Lazy because most shots in a gallery are only ever seen as a thumbnail: creating four object URLs
 * per shot up front would pin every pass of every shot in memory for as long as the page lives.
 */
export function blobImage(blob: Blob, register: (url: string) => void): ShotImage {
  let url: string | null = null;
  return {
    get url(): string {
      if (url === null) {
        url = URL.createObjectURL(blob);
        register(url);
      }
      return url;
    },
    blob: () => Promise.resolve(blob),
  };
}
